import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { Cell } from 'api';
import { useXlsxSpreadsheetNotesQuery } from 'components/Drawer/document-preview-drawer-tab/xlsx-viewer/useXlsxSpreadsheetNotesQuery';
import { XLSX_VIEWER_STRINGS } from 'components/Drawer/document-preview-drawer-tab/xlsx-viewer/xlsx-viewer.contants';
import { GridSkeleton } from 'components/Grid';
import WorkbookContext from 'components/ScalarSpreadsheet/utilities/WorkbookContext';
import useSpreadsheetNoteTabs from 'components/SpreadsheetNotes/SpreadsheetNoteConfig/UseSpreadsheetNoteTabs/useSpreadsheetNoteTabs';
import { TabsNavigation, Tab as TabType } from 'components/TabsNavigation';
import { useTabsMenu } from 'components/TabsNavigation/hooks/useTabsMenu';
import MappingActionsToolbar from './MappingActionsToolbar';
import { XLSXViewerTabProps } from './XLSXViewerTab.props';
import { EventType, useEventSubscription } from '../../../../context';
import { AttributeMappingType } from '../../../../pages/Financials/financials-widget-drawer/types';
import { FinancialsCellWithReferences } from '../../../../pages/Financials/types/cell-with-references';
import { useAcceptAllAIMappings, useDeclineAllAIMappings } from '../../../../services/hooks/attributeMapper';
import SheetAttributeManager from '../../../../services/sheet-attribute-manager/SheetAttributeManager';
import { useCellMappingStore } from '../../../../store';
import { useAttributeMapperStore } from '../../../../store/useAttributeMapperStore';
import { Logger } from '../../../../utilities';
import adjustAlphaFromColorString from '../../../../utilities/adjust-alpha-in-color-string/adjust-alpha-from-color-string';
import { convertedColors } from '../../../ScalarSpreadsheet/constants';
import { useAttributeMappedWidgetDrawer } from '../../attribute-mapped-widget-drawer/AttributeMappedWidgetDrawerContext';

const XLSXViewerTab = ({ document }: XLSXViewerTabProps) => {
  const { attributeMapper, attributeReferenceConfig, handlePushingAttributeMappings, handleClearMappings } =
    useAttributeMappedWidgetDrawer();
  const { updateCellMapping } = useCellMappingStore();
  const { setAttributeMapperManager } = useAttributeMapperStore();
  const [acceptAllAIMappings] = useAcceptAllAIMappings();
  const [declineAllAIMappings] = useDeclineAllAIMappings();
  const [activeTab, setActiveTab] = useState<TabType | null>(null);

  const hasPendingAISuggestions = useMemo(() => attributeMapper?.hasUnacceptedAIMappings() || false, [attributeMapper]);

  const handleAcceptAllAISuggestions = useCallback(async () => {
    const currentMappingId = attributeMapper?.sheetAttributeMapping.id;
    if (!currentMappingId) {
      enqueueSnackbar('No mapping ID found', { variant: 'error' });
      return;
    }
    try {
      const updatedMapping = await acceptAllAIMappings(currentMappingId);
      setAttributeMapperManager(new SheetAttributeManager(updatedMapping));
      enqueueSnackbar('All AI suggestions accepted successfully', { variant: 'success' });
    } catch (error) {
      Logger.error('XLSXViewerTab - Error accepting all AI mappings', error);
      enqueueSnackbar('Failed to accept AI suggestions', { variant: 'error' });
    }
  }, [attributeMapper, acceptAllAIMappings, setAttributeMapperManager]);

  const handleDeclineAllAISuggestions = useCallback(async () => {
    const currentMappingId = attributeMapper?.sheetAttributeMapping.id;
    if (!currentMappingId) {
      enqueueSnackbar('No mapping ID found', { variant: 'error' });
      return;
    }
    try {
      const updatedMapping = await declineAllAIMappings(currentMappingId);
      setAttributeMapperManager(new SheetAttributeManager(updatedMapping));
      enqueueSnackbar('All AI suggestions declined successfully', { variant: 'success' });
    } catch (error) {
      Logger.error('XLSXViewerTab - Error declining all AI mappings', error);
      enqueueSnackbar('Failed to decline AI suggestions', { variant: 'error' });
    }
  }, [attributeMapper, declineAllAIMappings, setAttributeMapperManager]);

  const { data, error, isLoading, isPolling } = useXlsxSpreadsheetNotesQuery(document.file.id);

  // pass notes into the store

  const notes = useMemo(() => {
    if (!data || data.length === 0) {
      return [];
    }
    return data;
  }, [data]);

  const { handleMenuOpen, handleMenuClose, menuAnchorEl } = useTabsMenu();

  const isAttributeMapperEnabled = !!attributeMapper;

  const { tabs, workbookContextValue, onNoteChange, cells } = useSpreadsheetNoteTabs({
    notes,
    actionsAreDisabled: false,
    isAttributeMapperEnabled,
    attributeReferenceConfig,
    attributeMapper,
    documentId: document.document_reference_id,
  });

  const handleActiveTabChange = (tab: TabType) => {
    setActiveTab(tab);
  };

  const clearHighlights = () => {
    if (!cells) {
      return;
    }

    Object.values(cells).forEach(sheet => {
      Object.values(sheet).forEach(cell => {
        cell.setCellBackgroundColor(undefined);
        // eslint-disable-next-line no-param-reassign
        cell.style = undefined;
        cell.dispatchEvent();
      });
    });
  };

  useEventSubscription(
    EventType.SPREADSHEET_ACTIVE_CELL_CHANGED,
    ([newCell, _prevCell]: [{ cellInfo: FinancialsCellWithReferences }, FinancialsCellWithReferences]) => {
      if (attributeReferenceConfig?.selectionMode === AttributeMappingType.SINGLE_CELL && attributeMapper) {
        Logger.debug('Handling single cell avoiding highlights');
        handlePushingAttributeMappings?.({
          documentReferencePayload: {
            documentReferenceId: document.document_reference_id,
            sheet: `${activeTab?.label}`,
          },
          selectedCell: newCell,
        });
      }

      clearHighlights();

      if (!cells || !newCell.cellInfo.mappingReferences?.length) {
        return;
      }
      const normalizationRegex = /[^a-z0-9]/g;

      // normalize the sheet names here to use as a look up for the original sheet name, to grab cells correctly
      const normalizedToOriginal = Object.keys(cells).reduce(
        (acc, sheetName) => {
          const normalized = sheetName.toLowerCase().replaceAll(normalizationRegex, '');
          acc[normalized] = sheetName;
          return acc;
        },
        {} as Record<string, string>
      );

      newCell.cellInfo.mappingReferences.forEach(item => {
        // the sheet names are normalized outside of this context upstream
        // in order to ensure we can find the cells to highlight on the right sheet
        // the name must be normalized
        const normalizedSheetName = item.sheet_name.toLowerCase().replaceAll(normalizationRegex, '');
        const originalSheetName = normalizedToOriginal[normalizedSheetName];
        const noteForUpdatedSheet = originalSheetName && cells[originalSheetName];
        if (noteForUpdatedSheet) {
          item.cell_references.forEach((cellRef, index) => {
            const updatedCell = noteForUpdatedSheet?.[cellRef.ref];
            const color = convertedColors[index];
            const borderColor = adjustAlphaFromColorString(color, 0.6);
            updatedCell.setCellBackgroundColor(color);
            updatedCell.style = { boxShadow: `inset 0 0 0 3px ${borderColor}` };
            updatedCell.dispatchEvent();
          });
        }
      });
    }
  );

  useEffect(() => {
    //   we return here to prevent updating the cell mapping when we don't
    // actually have any data. Also prevents bugs on initial load of sheet
    if (isLoading) {
      return;
    }
    // POC of propagating the cell mapping to the attribute mapper
    // Include document ID in the key to distinguish sheets from different documents with the same name
    const documentId = document.document_reference_id;
    const cellMapping = notes
      .filter(note => note.content)
      .reduce(
        (acc, note) => {
          const { cells: mappedCells } = note.content;
          const name = note.name ?? '';
          // Use composite key: documentId_sheetName to avoid conflicts when multiple documents have the same sheet name
          const compositeKey = `${documentId}_${name}`;
          acc[compositeKey] = mappedCells ?? {};
          // Also keep the original key for backward compatibility
          acc[name] = mappedCells ?? {};
          return acc;
        },
        {} as Record<string, Record<string, Cell>>
      );

    updateCellMapping(cellMapping);
  }, [notes, updateCellMapping, isLoading, document.document_reference_id]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === 'p') {
        e.preventDefault();
        handlePushingAttributeMappings?.({
          documentReferencePayload: {
            documentReferenceId: document.document_reference_id,
            sheet: `${activeTab?.label}`,
          },
        });
      }
    },
    [handlePushingAttributeMappings, activeTab?.label, document.document_reference_id]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  if (isLoading && !error && !isPolling) {
    return <GridSkeleton />;
  }

  if (isPolling) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight={200} gap={2}>
        <div>{XLSX_VIEWER_STRINGS.LOADING.MESSAGE}</div>
        <GridSkeleton />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <Alert severity="error" variant="filled">
          <strong>{XLSX_VIEWER_STRINGS.ERROR.TITLE}</strong>
          <div>{XLSX_VIEWER_STRINGS.ERROR.MESSAGE}</div>
        </Alert>
      </Box>
    );
  }

  return (
    <WorkbookContext.Provider value={workbookContextValue}>
      <TabsNavigation
        menuAnchorEl={menuAnchorEl}
        mountOnActive
        handleMenuClose={handleMenuClose}
        handleMenuOpen={handleMenuOpen}
        relativeDrawer
        rightAction={
          isAttributeMapperEnabled && handleClearMappings ? (
            <MappingActionsToolbar
              documentReferenceId={document.document_reference_id}
              activeSheetLabel={activeTab?.label as string | undefined}
              onPushMappings={() => handlePushingAttributeMappings?.()}
              onClearMappings={handleClearMappings}
              hasPendingAISuggestions={hasPendingAISuggestions}
              onAcceptAllAISuggestions={handleAcceptAllAISuggestions}
              onDeclineAllAISuggestions={handleDeclineAllAISuggestions}
            />
          ) : undefined
        }
        tabs={tabs}
        onActiveTabChange={handleActiveTabChange}
      />
    </WorkbookContext.Provider>
  );
};
export default XLSXViewerTab;
